import React, { useEffect, useMemo, useState } from "react";
import {
  Award, CalendarCheck, ChevronDown, ChevronRight, CircleHelp, CircleSlash, EyeOff, Hourglass, LineChart, Minus,
  Phone, Table2, TrendingDown, TrendingUp,
} from "lucide-react";
import { apiGet } from "../../../Api/Client.js";
import PersonPicker from "./PersonPicker.jsx";
import { VizStyles } from "./vizTheme.jsx";
import {
  BeforeNow, RunSquares, ScoreStrip, WeeklyScoreChart, WeeksTable, ZoneLegend, fmt,
} from "./GrowthCharts.jsx";
import { MISS_AT, TARGET, plural, shortDate } from "./callMeta.js";

// Growth — is this person getting better, and at what.
//
// Written for someone who has never read a chart for a living. The page
// answers four questions, in the order a rep asks them, each in a sentence
// before any picture:
//
//   1. "Am I getting better?"         one verdict, one number against the target
//   2. "Week by week?"                one line, one week per column, click a week
//                                     to see the calls behind it
//   3. "At what?"                     every skill, better / slipping / same /
//                                     too early, click one for every score
//   4. "What's going well?"           runs of calls without a miss
//
// The rule that decides whether this is useful or harmful is unchanged: a
// movement smaller than the scorer's own noise, or measured on too few calls,
// is never called a direction -- and the page says which of the two it was,
// in words, because "record more calls" and "nothing changed" are different
// things to hear.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px",
               minWidth: 0 };
const muted = { color: "var(--c-500)" };

const GROWTH_CSS = `
.gr-row { display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(120px, 1fr) auto; gap: 14px; align-items: center; }
.gr-facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.gr-hero { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); gap: 22px; align-items: center; }
.gr-seg { display: inline-flex; gap: 2px; padding: 2px; border: 1px solid var(--c-200); border-radius: 9px; flex-wrap: wrap; }
.gr-seg button { border: none; background: transparent; cursor: pointer; font: inherit; font-size: 12px;
  padding: 5px 10px; border-radius: 7px; color: var(--c-600, var(--c-500)); white-space: nowrap; }
.gr-seg button[aria-pressed="true"] { background: var(--c-100); color: var(--c-900); font-weight: 700; }
.gr-seg button:disabled { opacity: .45; cursor: default; }
.gr-link { border: 1px solid var(--c-200); background: transparent; border-radius: 8px; padding: 4px 9px;
  cursor: pointer; font: inherit; font-size: 12px; color: var(--c-700); display: inline-flex; align-items: center; gap: 5px; }
.gr-link:hover { background: var(--c-100); }
.gr-skill { width: 100%; text-align: left; background: transparent; border: none; font: inherit; cursor: pointer;
  padding: 9px 8px; border-radius: 9px; color: inherit; }
.gr-skill:hover, .gr-skill[aria-expanded="true"] { background: var(--c-100); }
.gr-call { display: grid; grid-template-columns: 54px minmax(0, 1fr) auto auto; gap: 10px; align-items: center;
  padding: 7px 8px; border-radius: 8px; border: none; background: transparent; width: 100%; text-align: left;
  font: inherit; color: inherit; cursor: pointer; }
.gr-call:hover { background: var(--c-100); }
@media (max-width: 720px) {
  .gr-hero { grid-template-columns: 1fr; gap: 14px; }
  .gr-facts { grid-template-columns: 1fr; }
  .gr-row { grid-template-columns: minmax(0, 1fr) auto; row-gap: 6px; }
  .gr-row .gr-track { grid-column: 1 / -1; grid-row: 2; }
  .gr-call { grid-template-columns: 48px minmax(0, 1fr) auto; }
  .gr-call .gr-call-type { display: none; }
}
`;

// ── Words ────────────────────────────────────────────────────────────────────

const SIDE = {
  tonality: { short: "How you sound", formal: "Voice & delivery" },
  technicality: { short: "What you say", formal: "Substance" },
  both: { short: "Sound + words", formal: "both" },
  outcome: { short: "From the CRM", formal: "outcome" },
};

const SERIES = [
  { key: "average", label: "Overall", long: "Average score" },
  { key: "tonality", label: "How you sound", long: "How-you-sound score" },
  { key: "technicality", label: "What you say", long: "What-you-say score" },
];

// verdict -> how it is drawn and said. Colour never carries it alone: every
// verdict has an icon and a word.
// "No clear change", not "holding steady": a move inside the noise may be a
// real move too small to see yet, and the page should not claim steadiness.
const VERDICT = {
  up: { word: "Getting better", chip: "Better", icon: TrendingUp, color: "var(--sr-good-text)", tone: "up" },
  down: { word: "Slipping", chip: "Slipping", icon: TrendingDown, color: "var(--sr-bad-text)", tone: "down" },
  flat: { word: "No clear change", chip: "No clear change", icon: Minus, color: "var(--c-700)", tone: "flat" },
  early: { word: "Too early to tell", chip: "Too early", icon: Hourglass, color: "var(--c-600, var(--c-500))", tone: "flat" },
  not_observed: { word: "Didn't come up", chip: "Didn't come up", icon: CircleSlash, color: "var(--c-600, var(--c-500))", tone: "flat" },
  not_measurable: { word: "Couldn't be measured", chip: "Not measured", icon: EyeOff, color: "var(--c-600, var(--c-500))", tone: "flat" },
};

// The server says which (growth.py `status`). The "since the start" overall is
// built without it, so it is still read from confident/reason below.
const STATUS_VERDICT = {
  improving: "up", slipping: "down", no_clear_change: "flat", too_few_calls: "early",
  not_observed: "not_observed", not_measurable: "not_measurable",
};

function verdictOf({ delta, confident, reason, status }) {
  if (status && STATUS_VERDICT[status]) return STATUS_VERDICT[status];
  if (confident && delta > 0) return "up";
  if (confident && delta < 0) return "down";
  if (delta != null && (reason === "below_resolution_floor" || reason === "within_noise")) return "flat";
  return "early";
}

function signed(delta) {
  return delta == null ? "" : `${delta > 0 ? "+" : delta < 0 ? "−" : "±"}${Math.abs(delta).toFixed(1)}`;
}

// ── The comparison periods, as one shape ─────────────────────────────────────

function buildPeriods(data) {
  const history = data.history || {};
  const out = {};
  for (const days of data.intervals || []) {
    const iv = data.by_interval?.[String(days)];
    if (!iv) continue;
    out[String(days)] = {
      key: String(days),
      chip: `${days} days`,
      now: `the last ${days} days`,
      before: `the ${days} days before`,
      overall: iv.overall || {},
      nowCalls: iv.calls_current, beforeCalls: iv.calls_previous,
      skills: (iv.parameters || []).map((p) => ({
        key: p.key, label: p.label, good: p.good, means: p.means, low: p.low, dimension: p.dimension,
        before: p.previous, beforeCalls: p.previous_calls, now: p.current, nowCalls: p.current_calls,
        delta: p.delta, confident: p.confident, reason: p.reason, status: p.status, threshold: p.threshold,
        unscoredNow: p.unscored_current || {}, unscoredBefore: p.unscored_previous || {},
      })),
    };
  }
  const sso = history.since_start_overall;
  if (sso) {
    out.start = {
      key: "start",
      chip: "Since the start",
      now: sso.now_calls ? `the latest ${plural(sso.now_calls, "call")}` : "the latest calls",
      before: sso.start_calls ? `the first ${plural(sso.start_calls, "call")}` : "the first calls",
      overall: { current: sso.now, previous: sso.start, current_calls: sso.now_calls,
                 previous_calls: sso.start_calls, delta: sso.delta, confident: sso.confident, reason: sso.reason },
      nowCalls: sso.now_calls, beforeCalls: sso.start_calls,
      beforeFrom: sso.start_from, nowTo: sso.now_to,
      skills: (history.since_start || []).map((s) => ({
        key: s.key, label: s.label, good: s.good, means: s.means, low: s.low, dimension: s.dimension,
        before: s.start, beforeCalls: s.start_calls, now: s.now, nowCalls: s.now_calls,
        delta: s.delta, confident: s.confident, reason: s.reason, status: s.status, threshold: s.threshold,
        from: s.start_from, to: s.now_to,
      })),
    };
  }
  return out;
}

// The comparison a reader lands on: the first one with enough calls on both
// sides to say anything. A rep two weeks in has nothing in "the 14 days
// before"; showing them "too early" when "since the start" has a real answer
// is the page choosing to be unhelpful.
function defaultPeriod(periods) {
  for (const key of ["14", "7", "30", "start"]) {
    const o = periods[key]?.overall;
    if (o && o.delta != null && o.reason !== "insufficient_calls") return key;
  }
  return periods["14"] ? "14" : Object.keys(periods)[0];
}

// ── Small pieces ─────────────────────────────────────────────────────────────

function SectionTitle({ children, sub, right }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10,
                  flexWrap: "wrap", marginBottom: 10 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--c-900)" }}>{children}</div>
        {sub && <div style={{ fontSize: 12.5, color: "var(--c-700)", marginTop: 3, maxWidth: 640 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

function Segmented({ label, options, value, onChange }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      {label && <span style={{ fontSize: 12, ...muted }}>{label}</span>}
      <div className="gr-seg" role="group" aria-label={label}>
        {options.map((o) => (
          <button key={o.value} type="button" aria-pressed={value === o.value} disabled={o.disabled}
                  title={o.title} onClick={() => onChange(o.value)}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function VerdictChip({ v, delta }) {
  const meta = VERDICT[v];
  const Icon = meta.icon;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700,
                   color: meta.color, whiteSpace: "nowrap" }}>
      <Icon size={14} /> {meta.chip}{(v === "up" || v === "down") ? ` ${signed(delta)}` : ""}
    </span>
  );
}

function ScorePill({ value, missAt = MISS_AT, target = TARGET }) {
  if (value == null) return <span style={muted}>—</span>;
  const bg = value >= target ? "var(--sr-good-wash-key)" : value <= missAt ? "var(--sr-bad-wash-key)" : "var(--c-100)";
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--c-900)", background: bg, borderRadius: 6,
                   padding: "2px 7px", fontVariantNumeric: "tabular-nums" }}>{fmt(value)}</span>
  );
}

function Fact({ icon: Icon, label, value, sub, action }) {
  return (
    <div style={{ border: "1px solid var(--c-200)", borderRadius: 11, padding: "10px 12px", minWidth: 0,
                  display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, ...muted }}>
        <Icon size={13} /> {label}
      </span>
      <span style={{ fontSize: 18, fontWeight: 700, color: "var(--c-900)" }}>{value}</span>
      {sub && <span style={{ fontSize: 12, color: "var(--c-700)" }}>{sub}</span>}
      {action}
    </div>
  );
}

function Meter({ now, before, target = TARGET }) {
  const pct = (v) => `${(Math.max(0, Math.min(10, v)) / 10) * 100}%`;
  return (
    <div>
      <div aria-hidden="true" style={{ position: "relative", height: 12, borderRadius: 999, background: "var(--c-100)" }}>
        {now != null && (
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: pct(now), borderRadius: 999,
                        background: "var(--sr-series-1)" }} />
        )}
        <div style={{ position: "absolute", left: pct(target), top: -4, width: 2, height: 20, borderRadius: 1,
                      background: "var(--sr-good-mark)" }} />
        {before != null && (
          <span style={{ position: "absolute", top: 0, left: `calc(${pct(before)} - 6px)`, width: 8, height: 8,
                         borderRadius: 999, border: "2px solid var(--c-900)", background: "var(--surface)" }} />
        )}
      </div>
      <div style={{ position: "relative", height: 16, marginTop: 4, fontSize: 11, ...muted }}>
        <span style={{ position: "absolute", left: 0 }}>0</span>
        <span style={{ position: "absolute", left: pct(target), transform: "translateX(-50%)", color: "var(--c-700)",
                       fontWeight: 650, whiteSpace: "nowrap" }}>target {target}</span>
        <span style={{ position: "absolute", right: 0 }}>10</span>
      </div>
    </div>
  );
}

// ── The page ─────────────────────────────────────────────────────────────────

export default function GrowthPanel({ employeeId = null, people = [], onSelectEmployee, onOpenCall }) {
  const [data, setData] = useState(null);
  const [dataKey, setDataKey] = useState(null);           // which person + filter `data` is for
  const [error, setError] = useState("");
  const [modality, setModality] = useState("");
  const [period, setPeriod] = useState(null);           // null = pick for them
  const [seriesKey, setSeriesKey] = useState("average");
  const [selectedWeek, setSelectedWeek] = useState(null);   // a week_start, not an index
  const [openSkill, setOpenSkill] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAllCalls, setShowAllCalls] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [reload, setReload] = useState(0);

  // A different person is a different story: start them from the top.
  useEffect(() => {
    setPeriod(null); setSelectedWeek(null); setOpenSkill(null); setSeriesKey("average"); setShowAllCalls(false);
  }, [employeeId]);
  useEffect(() => { setOpenSkill(null); setShowAllCalls(false); }, [modality]);

  const requestKey = `${employeeId ?? ""}|${modality}`;
  useEffect(() => {
    let cancelled = false;
    setRefetching(true);
    const q = new URLSearchParams();
    if (employeeId) q.set("employee", String(employeeId));
    if (modality) q.set("modality", modality);
    apiGet(`/RevOps/growth/?${q}`)
      .then((payload) => { if (!cancelled) { setData(payload); setDataKey(requestKey); setError(""); } })
      .catch((err) => { if (!cancelled) setError(err?.message || "Couldn't load growth."); })
      .finally(() => { if (!cancelled) setRefetching(false); });
    return () => { cancelled = true; };
  }, [employeeId, modality, reload]);   // eslint-disable-line react-hooks/exhaustive-deps

  const history = data?.history || {};
  // The server's own miss line wins when it sends one; the shared default is the fallback.
  const missAt = history.miss_threshold ?? MISS_AT;
  const target = TARGET;

  // Leading empty weeks are dropped (keeping at least four), so a rep two
  // weeks in sees their two weeks across the chart, not squeezed into its
  // right-hand corner behind ten empty ones.
  const weeks = useMemo(() => {
    const all = history.weeks || [];
    const first = all.findIndex((w) => w.calls > 0);
    if (first < 0) return all.slice(-4);
    return all.slice(Math.max(0, Math.min(first, all.length - 4)));
  }, [history.weeks]);

  const periods = useMemo(() => (data ? buildPeriods(data) : {}), [data]);
  const periodKey = period && periods[period] ? period : defaultPeriod(periods);
  const P = periods[periodKey];

  if (error && (!data || dataKey !== requestKey)) {
    return (
      <div style={card}>
        <div className="Login-Error" style={{ marginBottom: 10 }}>{error}</div>
        <button type="button" className="gr-link" onClick={() => setReload((n) => n + 1)}>Try again</button>
        <style>{GROWTH_CSS}</style>
      </div>
    );
  }
  // A refresh for the same person (another call type, a retry) keeps the figures on screen, dimmed;
  // a different person never sees the last person's name, scores or calls.
  const shownFor = dataKey == null ? null : dataKey.split("|")[0];
  if (!data || shownFor !== String(employeeId ?? "")) return <p style={muted}>Loading…</p>;

  // Who the sentences are about: "you" for a rep, the person's name for a
  // manager looking at someone else.
  const isSelf = data.is_self !== false;
  const first = (data.employee_name || "").trim().split(/\s+/)[0] || "This rep";
  const W = isSelf
    ? { you: "you", You: "You", your: "your", Your: "Your", have: "have" }
    : { you: first, You: first, your: `${first}'s`, Your: `${first}'s`, have: "has" };

  const rules = { minCalls: data.min_calls_for_trend ?? 3, floor: data.resolution_floor ?? 0.5,
                  minStreak: history.min_streak ?? 3 };
  const anyCalls = (history.total_calls || 0) > 0;
  const weeksWithCalls = weeks.some((w) => w.calls > 0);
  const seriesAvailable = (k) => weeks.some((w) => w[k] != null);
  const activeSeries = SERIES.find((s) => s.key === seriesKey && seriesAvailable(s.key)) || SERIES[0];
  const lastWithCalls = [...weeks.keys()].reverse().find((i) => weeks[i].calls > 0);
  const picked = weeks.findIndex((w) => w.week_start === selectedWeek);
  const weekIdx = picked >= 0 ? picked : lastWithCalls;
  const week = weekIdx != null ? weeks[weekIdx] : null;
  const skillPoints = Object.fromEntries((history.since_start || []).map((s) => [s.key, s.points || []]));

  return (
    <div className="sr-viz" style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0,
                                     opacity: refetching ? 0.6 : 1, transition: "opacity .15s" }}>
      <VizStyles />
      <style>{GROWTH_CSS}</style>

      {/* One filter row, above everything it scopes. */}
      <div style={{ ...card, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 16, fontWeight: 700,
                          color: "var(--c-900)" }}>
              <LineChart size={17} /> {isSelf ? "Your growth" : `${data.employee_name}'s growth`}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--c-700)", marginTop: 3 }}>
              {anyCalls
                ? `${plural(history.total_calls, "scored call")} since ${shortDate(history.first_call)}`
                  + (history.last_call ? ` · latest on ${shortDate(history.last_call)}` : "")
                : "No scored calls yet"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <PersonPicker people={people} value={employeeId} onChange={onSelectEmployee} />
            <button type="button" className="gr-link" aria-expanded={showHelp} onClick={() => setShowHelp((v) => !v)}>
              <CircleHelp size={13} /> How to read this
            </button>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 22px", alignItems: "center" }}>
          <Segmented label="Calls" value={modality} onChange={setModality}
                     options={[{ value: "", label: "All" }, { value: "call", label: "Phone calls" },
                               { value: "meeting", label: "Demos" }]} />
          {anyCalls && (
            <Segmented label="Compare" value={periodKey} onChange={setPeriod}
                       options={[...(data.intervals || []).map((d) => ({
                         value: String(d), label: `Last ${d} days`,
                         title: `The last ${d} days against the ${d} days before`,
                       })), ...(periods.start ? [{ value: "start", label: "First calls vs latest",
                                                   title: "Your first calls against your most recent ones" }] : [])]} />
          )}
        </div>
        {error && (
          <div style={{ fontSize: 12.5, color: "var(--sr-bad-text)" }}>
            Couldn't refresh: {error}{" "}
            <button type="button" className="gr-link" onClick={() => setReload((n) => n + 1)}>Try again</button>
          </div>
        )}
        {showHelp && <HowToRead missAt={missAt} target={target} floor={rules.floor} minCalls={rules.minCalls} />}
      </div>

      {!anyCalls ? (
        <div style={card}>
          <p style={{ ...muted, fontSize: 13.5, margin: 0 }}>
            Nothing to show yet{modality ? " for this kind of call" : ""}. This page fills in on its own as
            calls are scored.
          </p>
        </div>
      ) : (
        <>
          {P && <Hero P={P} W={W} target={target} history={history} onOpenCall={onOpenCall} rules={rules}
                onTryStart={periodKey !== "start" && periods.start ? () => setPeriod("start") : null} />}

          <div style={card}>
            <SectionTitle
              sub={weeksWithCalls ? `${W.Your} average score for each week, out of 10. The number under each week is `
                                    + "how many calls were scored. Click a week to see its calls." : null}
              right={weeksWithCalls && (
                <button type="button" className="gr-link" aria-pressed={showTable} onClick={() => setShowTable((v) => !v)}>
                  <Table2 size={12} /> {showTable ? "Hide table" : "Show as table"}
                </button>
              )}>
              Week by week
            </SectionTitle>
            {!weeksWithCalls ? (
              <p style={{ ...muted, fontSize: 13, margin: 0 }}>
                No calls scored in the last 12 weeks. {W.Your} latest scored call was on {shortDate(history.last_call)};
                the comparisons above and below still use every call from the past year.
              </p>
            ) : (<>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 18px", alignItems: "center",
                          justifyContent: "space-between", marginBottom: 8 }}>
              <Segmented value={activeSeries.key} onChange={setSeriesKey}
                         options={SERIES.map((s) => ({ value: s.key, label: s.label, disabled: !seriesAvailable(s.key),
                                                       title: s.key === "average" ? "Every skill together"
                                                         : `${SIDE[s.key].formal} skills only${seriesAvailable(s.key) ? "" : ": none scored yet"}` }))} />
              <ZoneLegend target={target} missAt={missAt} />
            </div>
            <WeeklyScoreChart weeks={weeks} seriesKey={activeSeries.key} seriesLabel={activeSeries.long}
                              selected={weekIdx} onSelect={(i) => { setSelectedWeek(weeks[i]?.week_start ?? null); setShowAllCalls(false); }}
                              target={target} missAt={missAt} />
            {week && <WeekCalls week={week} missAt={missAt} target={target} onOpenCall={onOpenCall}
                                showAll={showAllCalls} onShowAll={() => setShowAllCalls(true)} />}
            {showTable && <WeeksTable weeks={weeks} />}
            </>)}
          </div>

          {P && <Skills P={P} W={W} missAt={missAt} target={target} openSkill={openSkill} rules={rules}
                        onToggle={(k) => setOpenSkill((cur) => (cur === k ? null : k))}
                        points={skillPoints} onOpenCall={onOpenCall}
                        longer={["30", "start"].filter((k) => k !== periodKey && periods[k]
                                                         && (k !== "30" || periodKey === "7" || periodKey === "14"))
                                                .map((k) => ({ key: k, label: k === "start" ? "First calls vs latest" : "Last 30 days" }))}
                        onPeriod={setPeriod} />}

          {(history.streaks || []).length > 0 && <Streaks streaks={history.streaks} missAt={missAt} W={W} rules={rules} />}
        </>
      )}
    </div>
  );
}

// ── 1. The short answer ──────────────────────────────────────────────────────

function Hero({ P, W, target, history, onOpenCall, onTryStart, rules }) {
  const o = P?.overall || {};
  const v = verdictOf(o);
  const meta = VERDICT[v];
  const Icon = meta.icon;
  const nowCalls = o.current_calls ?? 0, beforeCalls = o.previous_calls ?? 0;
  const start = P.key === "start";
  // "the last 7 days (19 calls)"; for first-vs-latest the count is already in
  // the words, so it gets the dates instead.
  const nowText = start
    ? `${W.your} latest ${plural(nowCalls, "call")}${P.nowTo ? ` (up to ${shortDate(P.nowTo)})` : ""}`
    : `${P.now} (${plural(nowCalls, "call")})`;
  const beforeText = start
    ? `${W.your} first ${plural(beforeCalls, "call")}${P.beforeFrom ? ` (from ${shortDate(P.beforeFrom)})` : ""}`
    : `${P.before} (${plural(beforeCalls, "call")})`;

  let sentence;
  if (v === "up" || v === "down") {
    sentence = <>{W.Your} average score went {v === "up" ? "up" : "down"} from <strong>{fmt(o.previous)}</strong> to{" "}
      <strong>{fmt(o.current)}</strong> out of 10, comparing {nowText} with {beforeText}.</>;
  } else if (v === "flat") {
    sentence = <>{W.Your} average score was <strong>{fmt(o.previous)}</strong> across {beforeText} and{" "}
      <strong>{fmt(o.current)}</strong> across {nowText}. On these calls a move needs to be bigger than{" "}
      {o.threshold ?? rules.floor} to count: scores vary that much from call to call, and the scorer can move
      some of that just re-scoring the same call.</>;
  } else if (o.current == null) {
    sentence = <>No calls scored in {P.now}, so there is nothing new to compare.</>;
  } else if (start) {
    sentence = <>{W.Your} average so far is <strong>{fmt(o.current)}</strong>. To say whether that is better or worse
      we need at least {rules.minCalls} early calls and {rules.minCalls} recent ones; {W.you} {W.have}{" "}
      {plural(nowCalls + beforeCalls, "scored call")} so far.</>;
  } else {
    sentence = <>{W.Your} average in {P.now} is <strong>{fmt(o.current)}</strong>. To say whether that is better or
      worse we need at least {rules.minCalls} calls on both sides; {W.you} {W.have} {nowCalls} in {P.now} and{" "}
      {beforeCalls} in {P.before}.</>;
  }
  const lastCall = history.last_call ? new Date(`${String(history.last_call).slice(0, 10)}T00:00:00`) : null;
  const quietDays = lastCall ? Math.floor((Date.now() - lastCall.getTime()) / 86400000) : 0;

  const gap = o.current != null ? Math.round((target - o.current) * 10) / 10 : null;
  const best = history.best_call, bestWeek = history.best_week;

  return (
    <div style={card}>
      <div className="gr-hero">
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, ...muted, marginBottom: 4 }}>The short answer</div>
          <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 26, fontWeight: 750, color: meta.color,
                        lineHeight: 1.15 }}>
            <Icon size={26} strokeWidth={2.4} /> {meta.word}
          </div>
          <p style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "var(--c-700)" }}>{sentence}</p>
          {quietDays > 14 && (
            <p style={{ margin: "6px 0 0", fontSize: 12.5, ...muted }}>
              No calls scored since {shortDate(history.last_call)}.
            </p>
          )}
          {v === "early" && onTryStart && (
            <button type="button" className="gr-link" style={{ marginTop: 8 }} onClick={onTryStart}>
              Compare {W.your} first calls with {W.your} latest instead
            </button>
          )}
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 40, fontWeight: 750, color: "var(--c-900)", lineHeight: 1 }}>{fmt(o.current)}</span>
            <span style={{ fontSize: 14, ...muted }}>/ 10 {start ? "latest" : "now"}</span>
            {o.previous != null && <span style={{ fontSize: 12.5, ...muted, marginLeft: 6 }}>(○ was {fmt(o.previous)})</span>}
          </div>
          <Meter now={o.current} before={o.previous} target={target} />
          <div style={{ fontSize: 12.5, color: "var(--c-700)", marginTop: 4 }}>
            {gap == null ? "" : gap <= 0 ? "At or above the target." : `${gap.toFixed(1)} below the target of ${target}.`}
          </div>
        </div>
      </div>
      <div className="gr-facts" style={{ marginTop: 16 }}>
        <Fact icon={Phone} label="Calls in this comparison" value={`${nowCalls} vs ${beforeCalls}`}
              sub={`${P.now} vs ${P.before}`} />
        <Fact icon={Award} label="Best call" value={best ? `${fmt(best.average)} / 10` : "—"}
              sub={best ? `${best.company_name || best.call_label || "A call"} · ${shortDate(best.call_date)}`
                        : "Needs a call with 3 or more skills scored"}
              action={best && onOpenCall ? (
                <button type="button" className="gr-link" style={{ alignSelf: "flex-start", marginTop: 4 }}
                        onClick={() => onOpenCall(best.call_id)}>Open this call <ChevronRight size={12} /></button>
              ) : null} />
        <Fact icon={CalendarCheck} label="Best week" value={bestWeek ? `${fmt(bestWeek.average)} / 10` : "—"}
              sub={bestWeek ? `Week of ${shortDate(bestWeek.week_start)} · ${plural(bestWeek.calls, "call")}`
                            : `Needs a week with ${history.min_calls_per_week || 2} or more calls`} />
      </div>
    </div>
  );
}

// ── 2. The calls behind a week ───────────────────────────────────────────────

function WeekCalls({ week, missAt, target, onOpenCall, showAll, onShowAll }) {
  const list = week.call_list || [];
  const shown = showAll ? list : list.slice(0, 6);
  return (
    <div style={{ marginTop: 12, borderTop: "1px solid var(--c-200)", paddingTop: 12 }}>
      <div aria-live="polite" style={{ fontSize: 13, color: "var(--c-900)", fontWeight: 650 }}>
        Week of {shortDate(week.week_start)}
        <span style={{ fontWeight: 400, color: "var(--c-700)" }}>
          {week.calls
            ? ` · ${plural(week.calls, "call")} · average ${fmt(week.average)} · ${week.calls_with_miss} of ${week.calls} `
              + `had at least one miss`
            : " · no calls scored"}
        </span>
      </div>
      {week.thin && (
        <div style={{ fontSize: 12, ...muted, marginTop: 2 }}>Only one call this week, so it's drawn hollow: one call isn't a trend.</div>
      )}
      {list.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {shown.map((c) => (
            <button key={c.call_id} type="button" className="gr-call" onClick={() => onOpenCall?.(c.call_id)}
                    title="Open this call">
              <span style={{ fontSize: 12, ...muted }}>{shortDate(c.call_date)}</span>
              <span style={{ fontSize: 13, color: "var(--c-900)", overflow: "hidden", textOverflow: "ellipsis",
                             whiteSpace: "nowrap" }}>{c.company_name || c.call_label || "Call"}</span>
              <span className="gr-call-type" style={{ fontSize: 11.5, ...muted }}>
                {c.modality === "meeting" ? "Demo" : "Phone call"}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {c.misses > 0 && (
                  <span style={{ fontSize: 11, color: "var(--sr-bad-text)" }}
                        title={`${plural(c.misses, "skill")} scored ${missAt} or below on this call`}>
                    {c.misses === 1 ? "1 miss" : `${c.misses} misses`}
                  </span>
                )}
                <ScorePill value={c.average} missAt={missAt} target={target} />
              </span>
            </button>
          ))}
          {!showAll && list.length > shown.length && (
            <button type="button" className="gr-link" style={{ marginTop: 6 }} onClick={onShowAll}>
              Show all {list.length} calls
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── 3. Skill by skill ────────────────────────────────────────────────────────

const GROUPS = [
  { v: "up", title: "Getting better" },
  { v: "down", title: "Slipping" },
  { v: "flat", title: "No clear change" },
  { v: "early", title: "Too early to tell" },
  { v: "not_observed", title: "Didn't come up" },
  { v: "not_measurable", title: "Couldn't be measured" },
];

function unscoredCount(counts, kind) {
  return (counts && counts[kind]) || 0;
}

function skillSentence(s, P, W, rules) {
  const scope = P.key === "start"
    ? { before: `${W.your} first ${plural(s.beforeCalls, "call")} on it${s.from ? ` (from ${shortDate(s.from)})` : ""}`,
        now: `${W.your} latest ${plural(s.nowCalls, "call")}${s.to ? ` (to ${shortDate(s.to)})` : ""}` }
    : { before: `${P.before} (${plural(s.beforeCalls, "call")})`, now: `${P.now} (${plural(s.nowCalls, "call")})` };
  const v = verdictOf(s);
  if (v === "not_observed" || v === "not_measurable") {
    const n = unscoredCount(s.unscoredNow, v) + unscoredCount(s.unscoredBefore, v);
    return v === "not_observed"
      ? `It didn't come up on ${plural(n, "call")} in these periods (for example, no push-back to respond to), so there is nothing to compare.`
      : `It couldn't be measured on ${plural(n, "call")} in these periods (for example, the audio was too poor, or it isn't something we can measure yet), so there is nothing to compare.`;
  }
  if (s.before == null && s.now == null) return "Not scored in either period.";
  if (s.before == null) return `Scored ${fmt(s.now)} in ${scope.now}, and not at all in ${P.key === "start" ? "earlier calls" : P.before}, so there is nothing to compare it with yet.`;
  if (s.now == null) return `Scored ${fmt(s.before)} in ${scope.before}, and hasn't come up in ${P.now}.`;
  const base = `Averaged ${fmt(s.before)} in ${scope.before}, and ${fmt(s.now)} in ${scope.now}.`;
  if (s.confident) return base;
  if (s.reason === "below_resolution_floor") {
    return `${base} A move of ${Math.abs(s.delta).toFixed(1)} is smaller than ${rules.floor}, which the scorer can move just re-scoring the same call, so it isn't counted as a change.`;
  }
  if (s.reason === "within_noise") {
    return `${base} On these calls a move needs to be bigger than ${s.threshold} to count, because scores on this skill vary that much from call to call; ${Math.abs(s.delta).toFixed(1)} isn't, so there's no clear change yet.`;
  }
  return `${base} That's fewer than ${rules.minCalls} calls on one side, so it's too early to call a direction.`;
}

function Skills({ P, W, missAt, target, openSkill, onToggle, points, onOpenCall, longer = [], onPeriod, rules }) {
  const rows = P.skills.map((s) => ({ ...s, v: verdictOf(s) }));
  const groups = GROUPS.map((g) => ({
    ...g, rows: rows.filter((r) => r.v === g.v).sort((a, b) => Math.abs(b.delta || 0) - Math.abs(a.delta || 0)),
  })).filter((g) => g.rows.length);

  const period = P.key === "start"
    ? `Each skill: ${W.your} first calls on it against ${W.your} latest.`
    : `Each skill: ${P.now} against ${P.before}.`;

  return (
    <div style={card}>
      <SectionTitle sub={`${period} Click a skill to see what pulls it down, what a 10 looks like, and every score it got.`}>
        Skill by skill
      </SectionTitle>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11.5, color: "var(--c-700)", marginBottom: 6 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 999, border: "2px solid var(--c-500)" }} />before
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 999, background: "var(--sr-series-1)" }} />now
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span aria-hidden="true" style={{ width: 2, height: 12, background: "var(--sr-good-line)" }} />target {target}
        </span>
        <span style={muted}>each line runs 0 to 10</span>
      </div>
      {rows.length === 0 && <p style={{ ...muted, fontSize: 13, margin: "6px 0 0" }}>No skills scored in this comparison yet.</p>}
      {rows.length > 0 && rows.every((r) => r.v === "early") && longer.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 12.5,
                      color: "var(--c-700)", background: "var(--c-100)", borderRadius: 9, padding: "8px 10px", margin: "4px 0" }}>
          Each skill needs at least {rules.minCalls} calls on both sides before we call it. A longer comparison has more:
          {longer.map((l) => (
            <button key={l.key} type="button" className="gr-link" onClick={() => onPeriod(l.key)}>{l.label}</button>
          ))}
        </div>
      )}
      {groups.map((g) => (
        <div key={g.v} style={{ marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700,
                        color: VERDICT[g.v].color, padding: "0 8px 4px", borderBottom: "1px solid var(--c-200)" }}>
            {React.createElement(VERDICT[g.v].icon, { size: 14 })} {g.title}
            <span style={{ fontWeight: 400, ...muted }}>· {g.rows.length}</span>
          </div>
          {g.rows.map((s) => {
            const open = openSkill === s.key;
            const side = SIDE[s.dimension];
            return (
              <div key={s.key} style={{ borderBottom: "1px solid var(--c-200)" }}>
                <button type="button" className="gr-skill" aria-expanded={open} onClick={() => onToggle(s.key)}>
                  <div className="gr-row">
                    <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                      {open ? <ChevronDown size={14} style={{ ...muted, flexShrink: 0 }} />
                            : <ChevronRight size={14} style={{ ...muted, flexShrink: 0 }} />}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, color: "var(--c-900)", fontWeight: 600 }}>{s.label}</div>
                        {s.means && (
                          <div style={{ fontSize: 12, color: "var(--c-700)", lineHeight: 1.4, margin: "1px 0 2px" }}>
                            {s.means}
                          </div>
                        )}
                        <div style={{ fontSize: 11.5, ...muted }}>
                          {side ? side.short : ""}{side ? " · " : ""}{fmt(s.before)} → {fmt(s.now)}
                        </div>
                      </div>
                    </div>
                    <div className="gr-track"><BeforeNow before={s.before} now={s.now} tone={VERDICT[s.v].tone} target={target} /></div>
                    <VerdictChip v={s.v} delta={s.delta} />
                  </div>
                </button>
                {open && (
                  <div style={{ padding: "4px 10px 14px 30px", display: "flex", flexDirection: "column", gap: 8 }}>
                    {(s.low || s.good) && (
                      <div style={{ fontSize: 12.5, lineHeight: 1.45, color: "var(--c-900)", background: "var(--c-100)",
                                    borderRadius: 9, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
                        {s.low && (
                          <div><strong style={{ color: "var(--sr-bad-text)" }}>What pulls it down: </strong>{s.low}</div>
                        )}
                        {s.good && (
                          <div><strong style={{ color: "var(--sr-good-text)" }}>What a 10 looks like: </strong>{s.good}</div>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: 12.5, color: "var(--c-700)" }}>{skillSentence(s, P, W, rules)}</div>
                    {(points[s.key] || []).length > 0 && (
                      <>
                        <div style={{ fontSize: 12, ...muted }}>
                          Every score on this skill, oldest to newest{onOpenCall ? " · click a dot to open that call" : ""}
                        </div>
                        <ScoreStrip points={points[s.key]} target={target} missAt={missAt}
                                    onOpenCall={onOpenCall} label={s.label} />
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── 4. What's going well ─────────────────────────────────────────────────────

function Streaks({ streaks, missAt, W, rules }) {
  const anyCrm = streaks.some((s) => s.dimension === "outcome");
  return (
    <div style={card}>
      <SectionTitle sub={`A run is calls in a row where a skill scored above ${missAt}. Each square is one call`
                         + (anyCrm ? " (for skills measured from the CRM, one weekly CRM check)" : "")
                         + ", newest on the right; a cross is a miss. Calls where the skill didn't come up don't count either way."}>
        Runs without a miss
      </SectionTitle>
      {streaks.map((s) => { const unit = s.dimension === "outcome" ? "weekly check" : "call"; return (
        <div key={s.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                                  flexWrap: "wrap", padding: "9px 0", borderTop: "1px solid var(--c-200)" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--c-900)" }}>{s.label}</div>
            <div style={{ fontSize: 12.5, color: "var(--c-700)" }}>
              {s.current === 0
                ? `The latest ${unit} on this was a miss · best run ${s.best} in a row`
                : s.current < rules.minStreak
                  ? `${plural(s.current, `clean ${unit}`)} since the last miss · best run ${s.best} in a row`
                  : <>{plural(s.current, unit)} in a row without a miss{s.current >= s.best
                      ? <strong style={{ color: "var(--sr-good-text)" }}> · {W.your} best run yet</strong>
                      : ` · best run ${s.best}`}</>}
            </div>
          </div>
          <RunSquares recent={s.recent || []} missAt={missAt} />
        </div>
      ); })}
    </div>
  );
}

// ── How to read this page ────────────────────────────────────────────────────

function HowToRead({ missAt, target, floor, minCalls }) {
  const item = (title, body) => (
    <li style={{ marginBottom: 6 }}><strong style={{ color: "var(--c-900)" }}>{title}</strong> {body}</li>
  );
  return (
    <div style={{ background: "var(--c-100)", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "var(--c-700)" }}>
      <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
        {item("Scores run from 0 to 10.", `${target} is the target. A skill scoring ${missAt} or below on a call is a "miss".`)}
        {item("How you sound", "covers voice skills: pace, pauses, fillers, staying calm under push-back. The scorecard calls these \"Voice & delivery\".")}
        {item("What you say", "covers content skills: the questions you ask, turning pain into numbers, the demo. The scorecard calls these \"Substance\".")}
        {item("Better or slipping", `is only said when there are at least ${minCalls} calls on each side AND the score moved by ${floor} or more. `
              + "Smaller moves happen even when the scorer re-scores the very same call, so they aren't counted.")}
        {item("Hollow dots", "on the weekly chart mean only one call was scored that week. One call isn't a trend.")}
        {item("Click anything with an arrow or a dot", "to see the calls behind it, and open them.")}
      </ul>
    </div>
  );
}
